import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useToastContext } from '@/hooks/useToastContext';
import styles from './RegisterPage.module.css';

/**
 * Страница регистрации
 */
const RegisterPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { register } = useAuth();
  const { showError } = useToastContext();

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Валидация пароля
    if (password.length < 8) {
      setError('Пароль должен содержать минимум 8 символов');
      showError('Пароль должен содержать минимум 8 символов');
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      setError('Пароль должен содержать хотя бы одну заглавную букву, одну строчную букву и одну цифру');
      showError('Пароль должен содержать хотя бы одну заглавную букву, одну строчную букву и одну цифру');
      return;
    }

    if (password !== confirmPassword) {
      setError('Пароли не совпадают');
      showError('Пароли не совпадают');
      return;
    }

    setLoading(true);

    try {
      await register(email, password);
      // Навигация происходит в AuthContext
    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Произошла ошибка при регистрации';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [email, password, confirmPassword, register, showError]);

  const isFormValid = email.trim() !== ''
    && password.trim() !== ''
    && confirmPassword.trim() !== ''
    && password === confirmPassword;

  return (
    <div className={styles.container}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className={styles.content}
      >
        <motion.h1
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className={styles.title}
        >
          Регистрация
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className={styles.subtitle}
        >
          Создайте аккаунт для доступа к админ-панели
        </motion.p>

        <motion.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          onSubmit={handleSubmit}
          className={styles.form}
        >
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.label}>
              Email
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={styles.input}
                placeholder="Введите email"
                disabled={loading}
                autoComplete="email"
              />
            </label>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.label}>
              Пароль
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={styles.input}
                placeholder="Введите пароль"
                disabled={loading}
                autoComplete="new-password"
              />
              <div className={styles.hint}>
                Минимум 8 символов, должна быть заглавная буква, строчная буква и цифра
              </div>
            </label>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="confirmPassword" className={styles.label}>
              Подтвердите пароль
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className={styles.input}
                placeholder="Повторите пароль"
                disabled={loading}
                autoComplete="new-password"
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={!isFormValid || loading}
            className={styles.submitButton}
          >
            {loading ? 'Регистрация...' : 'Зарегистрироваться'}
          </button>

          <div className={styles.linkContainer}>
            <span>Уже есть аккаунт?</span>
            <Link to="/login" className={styles.link}>
              Войти
            </Link>
          </div>

          <div className={styles.linkContainer}>
            <Link to="/" className={styles.backLink}>
              ← Вернуться на главную
            </Link>
          </div>
        </motion.form>
      </motion.div>
    </div>
  );
};

export default RegisterPage;

